import { type ComponentType, type ErrorInfo } from "react";
import type { FallbackProps } from "react-error-boundary";
import { withErrorBoundary as withReactErrorBoundary } from "react-error-boundary";

import { DefaultFallbackComponent } from "./default-fallback-component";

export type ErrorHandler = (error: unknown, info: ErrorInfo, componentName: string) => void;

const defaultErrorHandler: ErrorHandler = (error, info, componentName) => {
	console.error(
		componentName,
		`An error occurred rendering the component. Exception details: ${JSON.stringify(error)} | Stack: ${JSON.stringify(info)}`,
		error,
		info,
	);
};

export const withErrorBoundary = <P extends object>(
	component: ComponentType<P>,
	componentToDisplayOnError?: ComponentType<FallbackProps> | null,
	errorHandler?: ErrorHandler,
	name?: string,
): ComponentType<P> => {
	const componentName = name || component.displayName || component.name || "Unknown";

	return withReactErrorBoundary(component, {
		FallbackComponent: componentToDisplayOnError ?? DefaultFallbackComponent,
		onError(error: unknown, info: ErrorInfo) {
			if (errorHandler) {
				errorHandler(error, info, componentName);
				return;
			}

			defaultErrorHandler(error, info, componentName);
		},
	}) as unknown as ComponentType<P>;
};
